import type { FormLayoutNode } from '../types/layout';
import type { FormFieldMapping } from '../types/fieldMapping';

/**
 * Collect dot-paths for all non-hidden fields under a layout subtree.
 */
export function collectFieldPathsFromLayout(
  nodes: FormLayoutNode[],
  namePrefix?: string,
): string[] {
  const paths: string[] = [];
  walkLayout(nodes, (field) => {
    paths.push(namePrefix ? `${namePrefix}.${field.name}` : field.name);
  });
  return paths;
}

function walkLayout(
  nodes: FormLayoutNode[],
  visit: (field: FormFieldMapping) => void,
  options?: { skipEachItemFields?: boolean },
): void {
  for (const node of nodes) {
    switch (node.type) {
      case 'fields':
        for (const entry of node.fields) {
          if ('type' in entry && (entry.type === 'spacer' || entry.type === 'section-divider')) continue;
          if ('type' in entry && entry.type === 'stack') {
            for (const subField of (entry as { fields: FormFieldMapping[] }).fields) {
              if (!subField.hidden) visit(subField);
            }
            continue;
          }
          const field = entry as FormFieldMapping;
          if (!field.hidden) visit(field);
        }
        break;
      case 'each':
        if (!options?.skipEachItemFields) {
          for (const field of node.itemFields) {
            if (!field.hidden) visit(field);
          }
        }
        break;
      case 'batch-table':
        for (const column of node.columns) {
          visit({
            name: `${node.name}[].${column.key}`,
            def: { label: column.label, schema: null, adapterHint: column.adapterHint ?? 'text' },
          });
        }
        break;
      case 'section':
      case 'grid':
      case 'stack':
      case 'surface':
        walkLayout(node.children, visit, options);
        break;
      case 'tabs':
        for (const tab of node.tabs) walkLayout(tab.children, visit, options);
        break;
      case 'conditional':
        walkLayout(node.children, visit, options);
        if (node.else) walkLayout(node.else, visit, options);
        break;
      case 'column-group':
        for (const group of node.groups) {
          for (const entry of group.fields) {
            if ('type' in entry && (entry.type === 'spacer' || entry.type === 'section-divider')) continue;
            const field = entry as FormFieldMapping;
            if (!field.hidden) visit(field);
          }
        }
        break;
      case 'data':
        walkLayout(node.children, visit, options);
        break;
      case 'udf':
        break;
      default:
        break;
    }
  }
}

/**
 * Collect field mappings from a layout subtree (non-hidden only).
 */
export function collectFieldsFromLayout(
  nodes: FormLayoutNode[],
  options?: { skipEachItemFields?: boolean },
): FormFieldMapping[] {
  const fields: FormFieldMapping[] = [];
  walkLayout(nodes, (field) => fields.push(field), options);
  return fields;
}

/**
 * Section visibility — do not treat nested `each` item fields as section-level fields.
 * Empty account arrays would otherwise hide the whole Brokerage / Transfer Agent block.
 */
export function collectSectionVisibilityFields(nodes: FormLayoutNode[]): FormFieldMapping[] {
  return collectFieldsFromLayout(nodes, { skipEachItemFields: true });
}
